import React, { useState } from 'react';
import { Info, ChevronDown, ChevronUp, Zap, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * RhetoricSection - Section 5: Rhetorical Devices & Strategic Framing
 *
 * Fields:
 * - Contrast Device (textarea): "At most schools... At [School]..."
 * - Mission Metaphor (textarea): Motto reinterpreted
 * - "What It's Not" Statement (textarea)
 * - Form-as-Function Idea (optional, text)
 * - Subversive Take (optional, textarea)
 */
export default function RhetoricSection({ data, onChange, collegeData, allSectionData }) {
  const [showGuidance, setShowGuidance] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const collegeName = collegeData?.global?.name || 'this school';

  // Get AI suggestions for rhetorical devices
  const getAISuggestions = async () => {
    // Validate we have college data
    if (!collegeData || !collegeData.id) {
      toast.error('College data not loaded yet. Please wait a moment and try again.');
      return;
    }

    // Check if we have content from previous sections
    if (!allSectionData?.ethos?.motto && !allSectionData?.personal?.micro_moment) {
      toast.error('Please complete earlier sections first so AI can craft rhetorical devices based on your content.');
      return;
    }

    setAiLoading(true);
    try {
      console.log('🎯 Requesting AI suggestions for rhetoric section:', {
        college_id: collegeData.id,
        has_section_data: {
          ethos: !!allSectionData?.ethos,
          academics: !!allSectionData?.academics,
          campus: !!allSectionData?.campus,
          personal: !!allSectionData?.personal
        }
      });

      const response = await fetch('/api/why-school/enhance-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          college_id: collegeData.id,
          section: 'rhetoric',
          mode: 'suggestions',
          existing_data: data,
          global_college_data: collegeData.global,
          previous_sections: {
            ethos: allSectionData?.ethos || {},
            academics: allSectionData?.academics || {},
            campus: allSectionData?.campus || {},
            personal: allSectionData?.personal || {}
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to get suggestions');
      }

      const suggestions = await response.json();
      console.log('✅ Received rhetoric suggestions:', suggestions);

      // Update with suggestions
      const updates = { ...data };
      let hasUpdates = false;

      if (suggestions.contrast_device && !data.contrast_device) {
        updates.contrast_device = suggestions.contrast_device;
        hasUpdates = true;
      }

      if (suggestions.mission_metaphor && !data.mission_metaphor) {
        updates.mission_metaphor = suggestions.mission_metaphor;
        hasUpdates = true;
      }

      if (suggestions.what_its_not && !data.what_its_not) {
        updates.what_its_not = suggestions.what_its_not;
        hasUpdates = true;
      }

      if (suggestions.form_as_function && !data.form_as_function) {
        updates.form_as_function = suggestions.form_as_function;
        hasUpdates = true;
      }

      if (suggestions.subversive_take && !data.subversive_take) {
        updates.subversive_take = suggestions.subversive_take;
        hasUpdates = true;
      }

      if (hasUpdates) {
        onChange(updates);
        toast.success('AI suggestions added! Review and edit to match your voice.');
      } else {
        toast.success('Loaded suggestions based on your earlier sections.');
      }
    } catch (err) {
      console.error('Error getting AI suggestions:', err);
      toast.error(`Could not load AI suggestions: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Add rhetorical sophistication to your essay by using strategic framing devices.
              These techniques help you position the school as uniquely aligned with your values—not
              just another good option.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Contrast Example:</p>
              <p className="italic">
                "At most schools, CS classes teach you to code faster. At MIT, they teach you to
                question what's worth building in the first place."
              </p>
            </div>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Metaphor Example:</p>
              <p className="italic">
                "'Mens et Manus' isn't just Latin—it's a permission slip to break things while thinking
                about why they were built that way."
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Info: Using Your Earlier Work */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Use what you've already written:</p>
            <p>
              Look back at your work in Sections 1-4. These rhetorical devices should reference
              your buzzwords, courses, spaces, or personal moment to create cohesion.
            </p>
          </div>
        </div>
      </div>

      {/* AI Suggestions Button */}
      <div className="flex justify-end">
        <button
          onClick={getAISuggestions}
          disabled={aiLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="AI will craft rhetorical devices based on your earlier sections"
        >
          {aiLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>Get AI Suggestions</span>
            </>
          )}
        </button>
      </div>

      {/* Field 1: Contrast Device */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Contrast Device
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Use the pattern "At most schools... At [School Name]..." to position this school as
          uniquely different. Be specific about what makes it distinct.
        </p>
        <textarea
          value={data.contrast_device || ''}
          onChange={(e) => onChange({ ...data, contrast_device: e.target.value })}
          placeholder={`Example: "At most schools, CS teaches you to code faster. At ${collegeName}, it teaches you to question what's worth building."`}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Field 2: Mission Metaphor */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          Mission Metaphor
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Reinterpret the school's motto or mission in your own language. Show what it means
          to you, not just what it officially means.
        </p>
        <textarea
          value={data.mission_metaphor || ''}
          onChange={(e) => onChange({ ...data, mission_metaphor: e.target.value })}
          placeholder="Example: &quot;'Mens et Manus' isn't just Latin - it's permission to break things while thinking about why they were built that way.&quot;"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[100px]"
          style={{ height: 'auto', minHeight: '100px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Field 3: "What It's Not" Statement */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">
          "What It's Not" Statement
        </label>
        <p className="text-sm text-gray-600 mb-3">
          Define the school by what it rejects or avoids. This can highlight its distinctive
          values by contrast with common approaches.
        </p>
        <textarea
          value={data.what_its_not || ''}
          onChange={(e) => onChange({ ...data, what_its_not: e.target.value })}
          placeholder={`Example: "${collegeName} isn't about grinding harder—it's about questioning whether you're solving the right problem."`}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[80px]"
          style={{ height: 'auto', minHeight: '80px' }}
          onInput={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
        />
      </div>

      {/* Optional Fields */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <span>Optional Advanced Devices</span>
          <span className="text-xs font-normal text-gray-500">(Use if they fit naturally)</span>
        </h3>

        {/* Field 4: Form-as-Function Idea */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Form-as-Function Idea
            <span className="ml-2 text-xs font-normal text-gray-500">Optional</span>
          </label>
          <p className="text-sm text-gray-600 mb-3">
            Find something where the school's structure mirrors its values. For example: "Open
            curriculum = trusting students to design their own learning path."
          </p>
          <input
            type="text"
            value={data.form_as_function || ''}
            onChange={(e) => onChange({ ...data, form_as_function: e.target.value })}
            placeholder="e.g., Open curriculum = trusting students to lead their education"
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Field 5: Subversive Take */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Subversive Take
            <span className="ml-2 text-xs font-normal text-gray-500">Optional</span>
          </label>
          <p className="text-sm text-gray-600 mb-3">
            Playfully challenge a stereotype or expectation about the school. Show you understand
            its culture deeply enough to make a knowing observation.
          </p>
          <textarea
            value={data.subversive_take || ''}
            onChange={(e) => onChange({ ...data, subversive_take: e.target.value })}
            placeholder={`Example: "Everyone says ${collegeName} is competitive, but I've learned it's actually competitive about collaboration—who can help others succeed better."`}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y min-h-[80px]"
            style={{ height: 'auto', minHeight: '80px' }}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
          />
        </div>
      </div>

      {/* Example/Tip Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          These rhetorical devices work best when they reference specific details from earlier sections.
          If you wrote about "questioning why we build" as a buzzword in Section 1, use that exact
          language in your contrast device here. This creates thematic cohesion throughout your essay.
        </p>
      </div>
    </div>
  );
}
